// src/lib/utils/events.ts

'use client'

import { useEffect, type RefObject } from 'react'

/**
 * Adds an event listener with passive option by default
 * @param {Element | Window | Document} element - The DOM element to attach the listener to
 * @param {string} event - The event name
 * @param {EventListenerOrEventListenerObject} handler - The event handler
 * @param {AddEventListenerOptions} [options={ passive: true }] - Event listener options
 * @returns {() => void} A cleanup function that removes the event listener
 */
export function addPassiveEventListener(
  element: Element | Window | Document,
  event: string,
  handler: EventListenerOrEventListenerObject,
  options: AddEventListenerOptions = { passive: true }
): () => void {
  element.addEventListener(event, handler, options)
  return () => element.removeEventListener(event, handler)
}

/**
 * Hook for handling scroll events with passive listeners
 * @param {EventListener} handler - The scroll event handler
 * @param {Element | Window | Document} [element=window] - Optional element to attach listener to (defaults to window)
 * @param {Object} [options] - Additional options for the scroll handler
 * @param {number} [options.debounce] - Optional debounce time in milliseconds
 * @returns {void}
 */
export function usePassiveScroll(
  handler: EventListener,
  element: Element | Window | Document = window,
  options?: { debounce?: number }
): void {
  useEffect(() => {
    if (options?.debounce) {
      let timeoutId: ReturnType<typeof setTimeout>
      const debouncedHandler: EventListener = (event) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => handler(event), options.debounce)
      }
      
      return addPassiveEventListener(element, 'scroll', debouncedHandler)
    }
    
    return addPassiveEventListener(element, 'scroll', handler)
  }, [handler, element, options?.debounce])
}

/**
 * Hook for handling window resize events with passive listeners
 * @param {EventListener} handler - The resize event handler
 * @param {Object} [options] - Additional options for the resize handler
 * @param {number} [options.debounce] - Optional debounce time in milliseconds
 * @returns {void}
 */
export function useWindowResize(
  handler: EventListener,
  options?: { debounce?: number }
): void {
  useEffect(() => {
    if (options?.debounce) {
      let timeoutId: ReturnType<typeof setTimeout>
      const debouncedHandler: EventListener = (event) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => handler(event), options.debounce)
      }
      
      return addPassiveEventListener(window, 'resize', debouncedHandler)
    }
    
    return addPassiveEventListener(window, 'resize', handler)
  }, [handler, options?.debounce])
}

/**
 * Hook for handling touch events with passive listeners
 * @param {(event: TouchEvent) => void} handler - The touch event handler
 * @param {Element | Window | Document} [element=document] - Optional element to attach listener to (defaults to document)
 * @returns {void}
 */
export function usePassiveTouch(
  handler: (event: TouchEvent) => void,
  element: Element | Window | Document = document
): void {
  useEffect(() => {
    const listener: EventListener = (event) => {
      if (event instanceof TouchEvent) {
        handler(event)
      }
    }
    return addPassiveEventListener(element, 'touchstart', listener)
  }, [handler, element])
}

/**
 * Hook for handling click outside events with passive listeners where appropriate
 * @param {RefObject<HTMLElement>} ref - Ref to the element to detect clicks outside of
 * @param {(event: MouseEvent | TouchEvent) => void} handler - The click outside handler
 * @returns {void}
 */
export function usePassiveClickOutside(
  ref: RefObject<HTMLElement>,
  handler: (event: MouseEvent | TouchEvent) => void
): void {
  useEffect(() => {
    const mouseListener: EventListener = (event) => {
      if (event instanceof MouseEvent && !ref.current?.contains(event.target as Node)) {
        handler(event)
      }
    }

    const touchListener: EventListener = (event) => {
      if (event instanceof TouchEvent && !ref.current?.contains(event.target as Node)) {
        handler(event)
      }
    }

    // Use passive for touch events, but not for mouse events
    addPassiveEventListener(document, 'touchstart', touchListener)
    document.addEventListener('mousedown', mouseListener, { passive: false })

    return () => {
      document.removeEventListener('touchstart', touchListener)
      document.removeEventListener('mousedown', mouseListener)
    }
  }, [ref, handler])
}

/**
 * Hook for observing element resize events
 * @param {RefObject<HTMLElement>} ref - Ref to the element to observe
 * @param {ResizeObserverCallback} callback - Callback function to execute when resize is detected
 * @returns {void}
 */
export function useResizeObserver(
  ref: RefObject<HTMLElement>,
  callback: ResizeObserverCallback
): void {
  useEffect(() => {
    if (!ref.current) return
    
    const observer = new ResizeObserver(callback)
    observer.observe(ref.current)
    
    return () => observer.disconnect()
  }, [ref, callback])
}

/**
 * Hook for observing element intersection with viewport
 * @param {RefObject<HTMLElement>} ref - Ref to the element to observe
 * @param {IntersectionObserverCallback} callback - Callback function to execute when intersection changes
 * @param {IntersectionObserverInit} [options] - Optional IntersectionObserver configuration options
 * @returns {void}
 */
export function useIntersectionObserver(
  ref: RefObject<HTMLElement>,
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): void {
  useEffect(() => {
    if (!ref.current) return
    
    const observer = new IntersectionObserver(callback, options)
    observer.observe(ref.current)
    
    return () => observer.disconnect()
  }, [ref, callback, options])
}

/**
 * Hook for handling keyboard key press events
 * @param {string | string[]} targetKey - Single key or array of keys to listen for
 * @param {(event: KeyboardEvent) => void} handler - Callback function to execute when specified key(s) are pressed
 * @param {Element | Window | Document} [element=document] - Optional element to attach listener to (defaults to document)
 * @returns {void}
 */
export function useKeyPress(
  targetKey: string | string[],
  handler: (event: KeyboardEvent) => void,
  element: Element | Window | Document = document
): void {
  useEffect(() => {
    const keyListener = (event: Event): void => {
      if (event instanceof KeyboardEvent) {
        const keys = Array.isArray(targetKey) ? targetKey : [targetKey]
        if (keys.includes(event.key)) {
          handler(event)
        }
      }
    }
    
    element.addEventListener('keydown', keyListener)
    return () => element.removeEventListener('keydown', keyListener)
  }, [targetKey, handler, element])
}

/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified wait time has elapsed since the last invocation.
 * 
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @param {Object} [options] - The options object
 * @param {boolean} [options.leading=false] - Specify invoking on the leading edge of the timeout
 * @param {boolean} [options.trailing=true] - Specify invoking on the trailing edge of the timeout
 * @param {boolean} [options.maxWait] - The maximum time func is allowed to be delayed before it's invoked
 * @returns {Function} The debounced function with cancel and flush methods
 */
export function debounce<T extends (...args: unknown[]) => ReturnType<T>>(
  func: T,
  wait = 0,
  options: { leading?: boolean; trailing?: boolean; maxWait?: number } = {}
): ((...args: Parameters<T>) => void) & { cancel: () => void; flush: () => ReturnType<T> | undefined } {
  let lastArgs: Parameters<T> | undefined
  let result: ReturnType<T> | undefined
  let timerId: ReturnType<typeof setTimeout> | undefined
  let lastCallTime: number | undefined
  let lastInvokeTime = 0
  let maxWait: number | undefined = options.maxWait
  const leading = !!options.leading
  const trailing = options.trailing !== false
  
  // Convert wait and maxWait to integers
  wait = +wait || 0
  if (maxWait !== undefined) {
    maxWait = +maxWait
  }
  
  function invokeFunc(time: number, thisArg: unknown, args: Parameters<T>): ReturnType<T> {
    lastInvokeTime = time
    result = func.apply(thisArg as ThisParameterType<T>, args)
    return result
  }
  
  function startTimer(pendingFunc: () => void, waitTime: number): ReturnType<typeof setTimeout> {
    return setTimeout(pendingFunc, waitTime)
  }
  
  function cancelTimer(id: ReturnType<typeof setTimeout> | undefined): void {
    if (id !== undefined) {
      clearTimeout(id)
    }
  }
  
  function leadingEdge(time: number, thisArg: unknown, args: Parameters<T>): void {
    // Reset any `maxWait` timer
    lastInvokeTime = time
    
    // Start the timer for the trailing edge
    timerId = startTimer(timerExpired, wait)
    
    // Invoke the leading edge
    if (leading) {
      invokeFunc(time, thisArg, args)
    }
  }
  
  function remainingWait(time: number): number {
    const timeSinceLastCall = time - (lastCallTime ?? 0)
    const timeSinceLastInvoke = time - lastInvokeTime
    const timeWaiting = wait - timeSinceLastCall
    
    return maxWait !== undefined
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting
  }
  
  function shouldInvoke(time: number): boolean {
    if (lastCallTime === undefined) {
      return true
    }
    
    const timeSinceLastCall = time - lastCallTime
    const timeSinceLastInvoke = time - lastInvokeTime
    
    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    )
  }
  
  function timerExpired(): void {
    const time = Date.now()
    if (shouldInvoke(time)) {
      trailingEdge(time)
      return
    }
    // Restart the timer
    timerId = startTimer(timerExpired, remainingWait(time))
  }
  
  function trailingEdge(time: number): void {
    timerId = undefined
    
    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once
    if (trailing && lastArgs) {
      invokeFunc(time, debounced, lastArgs)
    }
    lastArgs = undefined
  }
  
  function cancel(): void {
    if (timerId !== undefined) {
      cancelTimer(timerId)
    }
    lastInvokeTime = 0
    lastArgs = undefined
    lastCallTime = undefined
    timerId = undefined
  }
  
  function flush(): ReturnType<T> | undefined {
    return timerId === undefined ? result : (trailingEdge(Date.now()), result)
  }
  
  function debounced(this: unknown, ...args: Parameters<T>): void {
    const time = Date.now()
    const isInvoking = shouldInvoke(time)
    
    lastArgs = args
    lastCallTime = time
    
    if (isInvoking) {
      if (timerId === undefined) {
        leadingEdge(lastCallTime, this, args)
        return
      }
      if (maxWait !== undefined) {
        // Handle invocations in a tight loop
        timerId = startTimer(timerExpired, wait)
        invokeFunc(lastCallTime, this, args)
        return
      }
    }
    // If timerId is undefined, set it and start the timer
    timerId ??= startTimer(timerExpired, wait)
  }
  
  debounced.cancel = cancel
  debounced.flush = flush
  return debounced
}

/**
 * Note: For optimal performance, consider wrapping your event handlers
 * with useCallback before passing them to these hooks.
 * 
 * Example:
 * ```tsx
 * const handleScroll = useCallback((e: Event) => {
 *   // handle scroll
 * }, [dependencies]);
 * 
 * usePassiveScroll(handleScroll);
 * ```
 */